import styled from 'styled-components';
import { s } from './pay.style';
import './pay-complete-receipt.css';
import PageTop from '../components/pageTop/PageTop';
import Footer from '../components/footer/Footer';
import Payment from '../../service/pay/pay';
import { useNavigate, useLocation } from 'react-router-dom';
import { useEffect, useState, useRef } from 'react';
import { useModal } from '../../context/ModalContext';
import { Helmet } from 'react-helmet-async';
import Receipt from '../components/pay/receipt';
import { useUser } from '../../context/UserContext';

declare global {
  interface Window {
    gtag?: (...args: any[]) => void;
    fbq?: (...args: any[]) => void;
    wcs: any;
    wcs_add?: any;
  }
}

declare global {
  interface Window {
    fbq?: (...args: any[]) => void;
  }
}

interface DetailHistory {
  std_prc: number;
  is_discount: string;
  description: string;
  discount_amount: number;
  total_prc: number;
  good_name: string;
  order_id: string;
  plan_id: string;
  method: string;
  card_bank: string;
  installment: number;
  created_at: string;
  appl_num: number;
  account_num: string;
  bank_name: string;
  account_holder: string;
  input_date: string;
  input_time: string;
}

const PaymentComplete = () => {
  const { refreshUser } = useUser();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const payIdParam = searchParams.get('payId');
  const payId =
    payIdParam && !isNaN(Number(payIdParam)) ? Number(payIdParam) : null;
  const queryParams = new URLSearchParams(window.location.search);
  const paymentError = queryParams.get('error');
  const paymentPrice = queryParams.get('price');
  const price = queryParams.get('price');
  const oid = queryParams.get('oid');
  const plan_id = queryParams.get('plan_id');
  const good_name = queryParams.get('good_name');

  const [detailHistory, setDetailHistory] = useState<DetailHistory>();
  const [showToast, setShowToast] = useState(false);
  const [isReceiptModalOpen, setIsReceiptModalOpen] = useState(false);
  const printRef = useRef<HTMLDivElement>(null);
  const { modalDispatch } = useModal();

  const navigate = useNavigate();

  const formatDateTime = (dateStr?: string, timeStr?: string) => {
    if (!dateStr || dateStr.length !== 8) return '';
    const y = dateStr.slice(0, 4);
    const m = dateStr.slice(4, 6);
    const d = dateStr.slice(6, 8);

    let hh = '00';
    let mm = '00';
    if (timeStr && timeStr.length >= 4) {
      hh = timeStr.slice(0, 2);
      mm = timeStr.slice(2, 4);
    }
    return `${y}.${m}.${d} ${hh}:${mm}`;
  };

  const trackPurchaseScript = () => {
    if (!detailHistory) return;

    // Naver
    if (window.wcs) {
      if (!window.wcs_add) window.wcs_add = {};
      window.wcs_add['wa'] = 's_2489d344048e';

      const _conv = {
        type: 'purchase',
        value: detailHistory.total_prc,
        id: detailHistory.order_id,
        items: [
          {
            id: detailHistory.plan_id,
            name: detailHistory.good_name,
            payAmount: detailHistory.total_prc,
          },
        ],
      };

      window.wcs.trans(_conv);
    }

    // Facebook Pixel
    if (window.fbq) {
      window.fbq('track', 'Purchase', {
        value: detailHistory.total_prc,
        currency: 'KRW',
      });
    }
  };

  const getDetailHistory = async (payId: number) => {
    try {
      const response = await Payment.detailHistory(payId);
      setDetailHistory(response.data);
    } catch (error) {
      console.error('getDetailHistory 함수에서 에러 발생');
    }
  };

  const handleCopy = async () => {
    if (!detailHistory?.account_num) return;

    try {
      await navigator.clipboard.writeText(detailHistory?.account_num);
      setShowToast(true);
      setTimeout(() => setShowToast(false), 2500);
    } catch (err) {
      console.error('복사 실패:', err);
    }
  };

  useEffect(() => {
    if (payId) {
      getDetailHistory(payId);
      refreshUser();
    }
  }, [payId]);

  useEffect(() => {
    if (!paymentError && detailHistory) {
      trackPurchaseScript();
      if (window.gtag) {
        window.gtag('event', 'conversion', {
          send_to: 'AW-757069784/gXEpCP_Vgs8aENjv_-gC',
          value: detailHistory.total_prc,
          currency: 'KRW',
          transaction_id: detailHistory.order_id,
        });
      }
    }
  }, [detailHistory]);

  useEffect(() => {
    document.body.style.overflow = isReceiptModalOpen ? 'hidden' : 'auto';
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [isReceiptModalOpen]);

  const handlePrint = () => {
    if (!printRef.current) return;

    const printContents = printRef.current.innerHTML;

    const printWindow = window.open('', '', 'width=600,height=800');
    if (printWindow) {
      printWindow.document.open();
      printWindow.document.write(`
      <html>
        <head>
          <title>영수증</title>
          <link rel="stylesheet" href="/pay-complete-receipt.css" />
        </head>
        <body>
          <div class="receipt-container">
            ${printContents}
          </div>
        </body>
      </html>
    `);
      printWindow.document.close();
      printWindow.focus();

      setTimeout(() => {
        printWindow.print();
        printWindow.close();
      }, 500); // 렌더링 시간 확보
    }
  };

  return (
    <Container>
      <Helmet>
        <script type="text/javascript" src="//wcs.naver.net/wcslog.js"></script>
      </Helmet>

      <PageTop logo hasBackCloseButton={false} hasSearch={false} />
      {!paymentError ? (
        <s.Container>
          <s.PageTitleDiv>
            <img
              src="/assets/images/check/blue_check_48.svg"
              alt="check"
              style={{ width: '80px', height: '80px' }}
            />
            {detailHistory?.method === '가상계좌' ? (
              <span>결제대기</span>
            ) : (
              <span>결제완료</span>
            )}
          </s.PageTitleDiv>
          <div ref={printRef} className="receipt">
            <div className="total-price-box">
              <span className="text-500-14-light">총 결제 금액</span>
              <span className="text-700-20">
                {detailHistory?.total_prc.toLocaleString('ko-KR')}원
              </span>
            </div>

            <div className="content-column">
              <div className="info-row">
                <div className="text-500-16-light">기본 상품 금액</div>
                <div className="text-500-16">
                  {detailHistory?.std_prc.toLocaleString('ko-KR')}원
                </div>
              </div>
              <div className="info-row">
                <div className="text-500-16-light">할인 금액</div>
                <div className="text-500-16">
                  {detailHistory?.is_discount === 'Y' ? (
                    <>
                      <span>
                        {detailHistory.good_name.includes('바른NPL')
                          ? '바른NPL 50% 할인'
                          : detailHistory.good_name.includes('얼리버드')
                            ? '얼리버드 50% 할인'
                            : detailHistory.good_name.includes('공인중개사')
                              ? '공인중개사 50% 할인'
                              : '50% 할인'}
                      </span>
                      (-{detailHistory?.discount_amount.toLocaleString('ko-KR')}
                      원)
                    </>
                  ) : (
                    <>
                      {detailHistory?.discount_amount.toLocaleString('ko-KR')}원
                    </>
                  )}
                </div>
              </div>
              <div className="info-row">
                <div className="text-500-16-light">총 결제 금액</div>
                <div className="text-500-16">
                  {detailHistory?.total_prc.toLocaleString('ko-KR')}원
                </div>
              </div>
            </div>

            <div className="gray-line" />

            <div className="content-column">
              <div className="info-row">
                <div className="text-500-16-light">요금제 상품</div>
                <div className="text-500-16">{detailHistory?.good_name}</div>
              </div>

              {detailHistory?.method === '신용카드' && (
                <>
                  <div className="info-row">
                    <div className="text-500-16-light">결제카드</div>
                    <div className="text-500-16">
                      {detailHistory?.card_bank}
                    </div>
                  </div>
                  <div className="info-row">
                    <div className="text-500-16-light">할부기간</div>
                    <div className="text-500-16">
                      {detailHistory?.installment === 0 ? (
                        <span>일시불</span>
                      ) : (
                        <span>{detailHistory?.installment}개월</span>
                      )}
                    </div>
                  </div>
                  <div className="info-row">
                    <div className="text-500-16-light">결제일시</div>
                    <div className="text-500-16">
                      {detailHistory?.created_at.replaceAll('-', '.')}
                    </div>
                  </div>
                  <div className="info-row">
                    <div className="text-500-16-light">승인번호</div>
                    <div className="text-500-16">{detailHistory?.appl_num}</div>
                  </div>
                </>
              )}

              {detailHistory?.method === '실시간계좌이체' && (
                <div className="info-row">
                  <div className="text-500-16-light">결제일시</div>
                  <div className="text-500-16">
                    {detailHistory?.created_at.replaceAll('-', '.')}
                  </div>
                </div>
              )}

              {detailHistory?.method === '가상계좌' && (
                <>
                  <div className="info-row">
                    <div className="text-500-16-light">입금계좌</div>
                    <div className="data-column">
                      <div className="text-500-16">
                        {detailHistory?.account_num}
                      </div>
                      <div className="copy-button" onClick={handleCopy}>
                        계좌복사
                      </div>
                      {showToast && (
                        <span className="text-500-14-light blue-text">
                          계좌번호가 복사되었습니다.
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="info-row">
                    <div className="text-500-16-light">입금은행 / 예금주</div>
                    <div className="text-500-16">
                      {detailHistory?.bank_name} /{' '}
                      {detailHistory?.account_holder}
                    </div>
                  </div>
                  <div className="info-row">
                    <div className="text-500-16-light">입금기한</div>
                    <div className="data-column">
                      <div className="text-500-16">
                        {formatDateTime(
                          detailHistory?.input_date,
                          detailHistory?.input_time,
                        )}
                      </div>
                      <span className="text-500-14-light red-text">
                        기한 내 미입금 시 결제 자동 취소
                      </span>
                    </div>
                  </div>
                </>
              )}
            </div>

            {detailHistory?.method !== '가상계좌' && (
              <div className="button-box">
                <div
                  className="button"
                  onClick={() => modalDispatch({ type: 'I' })}
                >
                  환불문의
                </div>
                <div
                  className="button"
                  onClick={() => setIsReceiptModalOpen(!isReceiptModalOpen)}
                >
                  영수증
                </div>
              </div>
            )}

            <div className="blue100-button" onClick={() => navigate('/')}>
              확인
            </div>
          </div>
        </s.Container>
      ) : (
        <s.Container style={{ gap: '280px' }}>
          <s.PageTitleDiv>
            <img
              src="/assets/images/check/yellow_bang_80.svg"
              alt="check"
              style={{ width: '80px', height: '80px' }}
            />
            <span>결제실패</span>
            <s.PageTitleDescription>
              확인 후 다시 시도하시거나, 고객센터로 문의해 주세요.
            </s.PageTitleDescription>
          </s.PageTitleDiv>
          <RefundButtonBox>
            <s.Blue100Button
              onClick={() => {
                navigate('/payment/plan');
              }}
            >
              결제 다시하기
            </s.Blue100Button>
            <ToMainPageButton
              onClick={() => {
                navigate('/');
              }}
            >
              홈으로 돌아가기
            </ToMainPageButton>
          </RefundButtonBox>
        </s.Container>
      )}
      {isReceiptModalOpen && detailHistory && (
        <Receipt
          onClose={() => setIsReceiptModalOpen(false)}
          detailHistory={detailHistory}
        />
      )}
      <Footer />
    </Container>
  );
};

export default PaymentComplete;

const TotalPriceBox = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const ContentColumn = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const InfoRow = styled.div`
  width: 100%;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
`;

const DataColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  align-items: end;
`;

const CopyButton = styled.div`
  padding: 2px 6px;
  border: 1px solid #e5e8ea;
  border-radius: 2px;
  font-size: 13px;
`;

const ButtonBox = styled.div`
  padding: 24px 0px 40px;
  display: flex;
  flex-direction: row;
  gap: 16px;
  justify-content: flex-end;
`;

const Button = styled.div`
  padding: 9px 24px;
  background-color: rgba(112, 115, 124, 0.08);
  font-size: 16px;
  font-weight: 500;
  color: #616161;
  border-radius: 8px;
`;

const RefundButtonBox = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  font-weight: 700;
  gap: 24px;
`;

const ToMainPageButton = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  color: #008aff;
  font-size: 16px;
  font-weight: 700;
  border: 1px solid #008aff;
  border-radius: 8px;
  cursor: pointer;
  padding: 12px 28px;
`;

const Container = styled.div`
  max-width: 900px;
  min-height: 100vh;
  margin: 0 auto;
  box-shadow: 0 0 20px hsla(0, 0%, 51%, 0.15);
  overflow: hidden;
  position: relative;
  display: flex;
  flex-direction: column;
`;
